import React from "react";

import { useFormik } from "formik";
import * as Yup from "yup";
import AirDatepicker from "air-datepicker";
import moment from "moment";
import { Link } from "react-router-dom";

import localeEn from "air-datepicker/locale/en";
import "air-datepicker/air-datepicker.css";

import {
  ActivationsContext,
  ActivationsContextType,
} from "../../pages/activations/Context";

const requiredDateForm = "DD/MM/YYYY";

const dateOfBirthSchema = Yup.object().shape({
  dateOfBirth: Yup.string()
    .test("dateRequired", "Must be in a format like 04/12/1980", (value) => {
      const parsedDate = moment(value, requiredDateForm, true);
      return parsedDate.isValid();
    })
    .required("Date of birth is required"),
});

export type FormValuesType = {
  dateOfBirth: string;
};

type PropsType = {
  onSubmit: (details: FormValuesType) => Promise<void>;
};

export default function RequestDateOfBirthForm({ onSubmit }: PropsType) {
  const [appData, setAppData] =
    React.useContext<ActivationsContextType>(ActivationsContext);
  const [picker, setPicker] = React.useState<null | AirDatepicker>(null);

  const dateRef = React.useRef(null);

  const formik = useFormik({
    initialValues: {
      dateOfBirth: "",
    },
    onSubmit,
    validationSchema: dateOfBirthSchema,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const parsedDate = moment(value, requiredDateForm, true);

    formik.setFieldValue("dateOfBirth", value);

    if (parsedDate.isValid() === true && picker !== null) {
      const date = parsedDate.toDate();
      picker.setFocusDate(date, {
        viewDateTransition: true,
      });
      picker.selectDate(date);
    } else {
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    formik.submitForm();
  };

  React.useEffect(() => {
    if (dateRef.current === null) {
      return;
    }

    try {
      const airDatePicker = new AirDatepicker(dateRef.current, {
        locale: localeEn,
        startDate: moment().subtract(50, "years").toDate(),
        onSelect: (data) => {
          const pickedDate = (() => {
            if (Array.isArray(data.date)) {
              return data.date[0];
            }

            return data.date;
          })();

          formik.setFieldValue(
            "dateOfBirth",
            moment(pickedDate).format(requiredDateForm)
          );
        },
      });

      setPicker(airDatePicker);
    } catch (e) {
      console.warn("Airdate Picker triggered error, was:");
      console.error(e);

      if (picker) {
        picker.destroy();
      }
    }

    return () => {
      if (picker) {
        picker.destroy();
      }
    };
  }, []);

  return (
    <form
      autoComplete="off"
      data-testid="fetch-details-dob-form"
      onSubmit={handleSubmit}
    >
      <div>
        <input
          ref={dateRef}
          type="text"
          name="dateOfBirth"
          data-testid="dateOfBirth"
          id="dateOfBirth"
          className="p-2 w-96 border rounded border-gray-600 block mx-auto"
          value={formik.values.dateOfBirth}
          onChange={handleChange}
          placeholder={`e.g. ${moment()
            .subtract(50, "years")
            .format("DD/MM/YYYY")}`}
        />
        {formik.errors.dateOfBirth && formik.touched.dateOfBirth ? (
          <div className="mt-4 text-red-500 text-center font-bold">
            {formik.errors.dateOfBirth}
          </div>
        ) : null}
      </div>
      <div className="flex items-center mt-4 justify-center gap-4">
        <Link
          to={`/activations/enrol?campaignmember_id=${appData.params.campaignmember_id}`}
          className="btn btn-secondary"
        >
          No, I'll enter them manually
        </Link>
        <button className="btn btn-primary">Yes, retrieve my details</button>
      </div>
    </form>
  );
}
